/**
 * プロジェクト一覧の「場所」（Finder ソースリスト）を URL searchParams で表現する。
 * `/projects/:id` と衝突しないよう pathname は触らない。
 */

export type ProjectsLocation =
  | { kind: 'all' }
  | { kind: 'folder'; folderId: string }
  | { kind: 'uncategorized' }
  | { kind: 'trash' }

const LOCATION_PARAM = 'location'
const LOCATION_UNCATEGORIZED = 'uncategorized'
const LOCATION_TRASH = 'trash'

/** dnd-kit droppable id（メインのフォルダは UUID そのもの） */
export const DROP_UNCATEGORIZED = '__uncategorized__'
export const DROP_TRASH = '__trash__'

/** サイドバー用（メインの同名 droppable と ID 衝突しないようにする） */
const SIDEBAR_DROP_PREFIX = 'sidebar:'

export function sidebarDropId(dropId: string): string {
  return `${SIDEBAR_DROP_PREFIX}${dropId}`
}

function normalizeDropId(dropId: string): string {
  return dropId.startsWith(SIDEBAR_DROP_PREFIX) ? dropId.slice(SIDEBAR_DROP_PREFIX.length) : dropId
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function isFolderLocationId(value: string): boolean {
  return UUID_RE.test(value)
}

/**
 * `?location=` をパースする。未指定・空・不正値はすべて `{ kind: 'all' }`。
 */
export function parseProjectsLocation(
  searchParams: URLSearchParams | { get: (key: string) => string | null },
): ProjectsLocation {
  const raw = searchParams.get(LOCATION_PARAM)
  if (raw === null || raw === '') return { kind: 'all' }
  if (raw === LOCATION_TRASH) return { kind: 'trash' }
  if (raw === LOCATION_UNCATEGORIZED) return { kind: 'uncategorized' }
  if (isFolderLocationId(raw)) return { kind: 'folder', folderId: raw }
  return { kind: 'all' }
}

/** `/projects` への相対パス（クエリ付き） */
export function projectsLocationHref(location: ProjectsLocation): string {
  switch (location.kind) {
    case 'all':
      return '/projects'
    case 'trash':
      return `/projects?${LOCATION_PARAM}=${LOCATION_TRASH}`
    case 'uncategorized':
      return `/projects?${LOCATION_PARAM}=${LOCATION_UNCATEGORIZED}`
    case 'folder':
      return `/projects?${LOCATION_PARAM}=${location.folderId}`
  }
}

/** プロジェクトの所属からサイドバーでハイライトすべき場所を決める */
export function locationForProjectFolder(folderId: string | null): ProjectsLocation {
  if (folderId === null) return { kind: 'uncategorized' }
  return { kind: 'folder', folderId }
}

export function sameProjectsLocation(a: ProjectsLocation, b: ProjectsLocation): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'folder' && b.kind === 'folder') return a.folderId === b.folderId
  return true
}

/** droppable id → 移動先 folderId（ゴミ箱は null ではなく専用扱い） */
export function folderIdFromDropId(dropId: string): string | null | undefined {
  const id = normalizeDropId(dropId)
  if (id === DROP_UNCATEGORIZED) return null
  if (id === DROP_TRASH) return undefined
  if (isFolderLocationId(id)) return id
  return undefined
}

export function isTrashDropId(dropId: string): boolean {
  return normalizeDropId(dropId) === DROP_TRASH
}
